import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,faTrashAlt,
  faEnvelope,
  faLock,
  faCalendarAlt,
  faPhone,
  faCamera,
  faSave,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './Settings.css';

const Settings = ({ userData }) => {
  const [editMode, setEditMode] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    mobileNumber: '',
    profilePicture: null,
    previewImage: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [messages, setMessages] = useState({
    profile: { text: '', type: '' },
    password: { text: '', type: '' },
    image: { text: '', type: '' }
  });

  const [isSaving, setIsSaving] = useState({
    profile: false,
    password: false,
    image: false
  });

  useEffect(() => {
    const fetchTeacherData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/teacher/teachersettings/${userData.id}`);
        const teacherData = response.data.teacher;

        const fetchedData = {
          username: userData.username || '',
          firstName: teacherData.firstName || '',
          lastName: teacherData.lastName || '',
          dateOfBirth: teacherData.birth ? teacherData.birth.split('T')[0] : '',
          email: teacherData.email || '',
          mobileNumber: teacherData.mobileNumber || '',
          profilePicture: null,
          previewImage: teacherData.image || ''
        };

        setFormData(fetchedData);
        setInitialData(fetchedData);
      } catch (error) {
        setMessages(prev => ({
          ...prev,
          profile: { text: 'Failed to load teacher data', type: 'error' }
        }));
      }
    };

    if (userData?.id) {
      fetchTeacherData();
    }
  }, [userData]);

  const handleEditClick = (e) => {
    e.preventDefault();
    setEditMode(true);
    setMessages({
      profile: { text: '', type: '' },
      password: { text: '', type: '' },
      image: { text: '', type: '' }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleRemovePicture = async () => {
    try {
      setIsSaving(prev => ({ ...prev, image: true }));
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/teacher/teachersettings/removeimage/${userData.id}`);
      
      setFormData(prev => ({
        ...prev,
        profilePicture: null,
        previewImage: ''
      }));
      
      setMessages(prev => ({
        ...prev,
        image: { text: 'Profile picture removed successfully!', type: 'success' }
      }));
    } catch (error) {
      setMessages(prev => ({
        ...prev,
        image: { text: error.response?.data?.message || 'Error removing profile picture', type: 'error' }
      }));
    } finally {
      setIsSaving(prev => ({ ...prev, image: false }));
    }
  };
  
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        profilePicture: file,
        previewImage: URL.createObjectURL(file)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessages({
      profile: { text: '', type: '' },
      password: { text: '', type: '' },
      image: { text: '', type: '' }
    });

    // Save profile info
    if (formData.dateOfBirth !== initialData.dateOfBirth) {
      try {
        setIsSaving(prev => ({ ...prev, profile: true }));
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/teacher/teachersettings/teacherinfo`, {
          dateOfBirth: formData.dateOfBirth,
          teacherId: userData.id
        });
        setMessages(prev => ({
          ...prev,
          profile: { text: 'Profile updated successfully!', type: 'success' }
        }));
      } catch (error) {
        setMessages(prev => ({
          ...prev,
          profile: { text: error.response?.data?.message || 'Error updating profile', type: 'error' }
        }));
      } finally {
        setIsSaving(prev => ({ ...prev, profile: false }));
      }
    }

    // Save image
    if (formData.profilePicture) {
      try {
        setIsSaving(prev => ({ ...prev, image: true }));
        const formDataToSend = new FormData();
        formDataToSend.append('profilePicture', formData.profilePicture);
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/teacher/teachersettings/image/${userData.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setMessages(prev => ({
          ...prev,
          image: { text: 'Profile picture updated successfully!', type: 'success' }
        }));
      } catch (error) {
        setMessages(prev => ({
          ...prev,
          image: { text: error.response?.data?.message || 'Error updating profile picture', type: 'error' }
        }));
      } finally {
        setIsSaving(prev => ({ ...prev, image: false }));
      }
    }

    // Save password
    if (passwordData.currentPassword && passwordData.newPassword) {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setMessages(prev => ({
          ...prev,
          password: { text: 'Passwords do not match', type: 'error' }
        }));
        return;
      }

      try {
        setIsSaving(prev => ({ ...prev, password: true }));
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/teacher/teachersettings/passwordchange`, {
          teacherId: userData.id,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        });
        setMessages(prev => ({
          ...prev,
          password: { text: 'Password changed successfully!', type: 'success' }
        }));
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } catch (error) {
        setMessages(prev => ({
          ...prev,
          password: { text: error.response?.data?.message || 'Error changing password', type: 'error' }
        }));
      } finally {
        setIsSaving(prev => ({ ...prev, password: false }));
      }
    }

    // Only exit edit mode if all operations completed without errors
    const hasErrors = Object.values(messages).some(msg => msg.type === 'error');
    if (!hasErrors) {
      setTimeout(() => {
        setEditMode(false);
      }, 1500);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData(initialData);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setMessages({
      profile: { text: '', type: '' },
      password: { text: '', type: '' },
      image: { text: '', type: '' }
    });
  };

  const isSaveDisabled = () => {
    const noProfileChanges = formData.dateOfBirth === initialData.dateOfBirth && !formData.profilePicture;
    const noPasswordChanges = !passwordData.currentPassword && !passwordData.newPassword;
    return noProfileChanges && noPasswordChanges;
  };

  return (
    <div className="teacher-settings-container">
      <h2>Account Settings</h2>

      <form onSubmit={handleSubmit}>
        <div className="teacher-settings-section">
          <h3>Personal Information</h3>

          <div className="teacher-form-group">
            <label><FontAwesomeIcon icon={faUser} /> Username</label>
            <input type="text" value={formData.username} readOnly className="teacher-read-only" />
          </div>

          <div className="teacher-form-row">
            <div className="teacher-form-group">
              <label><FontAwesomeIcon icon={faUser} /> First Name</label>
              <input type="text" value={formData.firstName} readOnly className="teacher-read-only" />
            </div>
            <div className="teacher-form-group">
              <label><FontAwesomeIcon icon={faUser} /> Last Name</label>
              <input type="text" value={formData.lastName} readOnly className="teacher-read-only" />
            </div>
          </div>

          <div className="teacher-form-row">
            <div className="teacher-form-group">
              <label><FontAwesomeIcon icon={faPhone} /> Mobile Number</label>
              <input type="text" value={formData.mobileNumber} readOnly className="teacher-read-only" />
            </div>
            <div className="teacher-form-group">
              <label><FontAwesomeIcon icon={faEnvelope} /> Email</label>
              <input type="email" value={formData.email} readOnly className="teacher-read-only" />
            </div>
          </div>

          <div className="teacher-form-row">
            <div className="teacher-form-group">
              <label><FontAwesomeIcon icon={faCalendarAlt} /> Date of Birth</label>
              <input 
                type="date" 
                name="dateOfBirth" 
                value={formData.dateOfBirth} 
                onChange={handleInputChange} 
                disabled={!editMode} 
              />
            </div>
          </div>

          {messages.profile.text && (
            <div className={`teacher-message teacher-message-${messages.profile.type}`}>
              {messages.profile.text}
            </div>
          )}
        </div>

        <div className="teacher-settings-section">
          <h3>Change Password</h3>
          <div className="teacher-form-group">
            <label><FontAwesomeIcon icon={faLock} /> Current Password</label>
            <input 
              type="password" 
              name="currentPassword" 
              value={passwordData.currentPassword} 
              onChange={handlePasswordChange} 
              disabled={!editMode} 
            />
          </div>
          <div className="teacher-form-group">
            <label><FontAwesomeIcon icon={faLock} /> New Password</label>
            <input 
              type="password" 
              name="newPassword" 
              value={passwordData.newPassword} 
              onChange={handlePasswordChange} 
              disabled={!editMode} 
            />
          </div>
          <div className="teacher-form-group">
            <label><FontAwesomeIcon icon={faLock} /> Confirm New Password</label>
            <input 
              type="password" 
              name="confirmPassword" 
              value={passwordData.confirmPassword} 
              onChange={handlePasswordChange} 
              disabled={!editMode} 
            />
          </div>

          {messages.password.text && (
            <div className={`teacher-message teacher-message-${messages.password.type}`}>
              {messages.password.text}
            </div>
          )}
        </div>

        <div className="teacher-settings-section">
  <h3>Profile Picture</h3>
  <div className="teacher-profile-picture-container">
    <div className="teacher-profile-picture-preview">
      {formData.previewImage ? (
        <img src={formData.previewImage} alt="Profile" />
      ) : (
        <div className="teacher-profile-picture-placeholder">
          <FontAwesomeIcon icon={faUser} size="3x" />
        </div>
      )}
    </div>
    <div className="teacher-profile-picture-upload">
      <label className="teacher-upload-button">
        <FontAwesomeIcon icon={faCamera} /> Change Photo
        <input 
          type="file" 
          accept="image/*" 
          onChange={handleImageChange} 
          disabled={!editMode} 
          style={{ display: 'none' }} 
        />
      </label>
      {formData.previewImage && editMode && (
        <button 
          type="button" 
          className="teacher-remove-button"
          onClick={handleRemovePicture}
          disabled={isSaving.image}
        >
          <FontAwesomeIcon icon={faTrashAlt} /> Remove Photo
        </button>
      )}
      <span className="teacher-upload-note">Recommended size: 200x200 pixels</span>
    </div>
  </div>

  {messages.image.text && (
    <div className={`teacher-message teacher-message-${messages.image.type}`}>
      {messages.image.text}
    </div>
  )}
</div>

        <div className="teacher-settings-actions">
          {!editMode ? (
            <button type="button" className="teacher-edit-button" onClick={handleEditClick}>
              <FontAwesomeIcon icon={faEdit} /> Edit Profile
            </button>
          ) : (
            <>
              <button 
                type="submit" 
                className="teacher-save-button" 
                disabled={isSaving.profile || isSaving.password || isSaving.image || isSaveDisabled()}
              >
                {isSaving.profile || isSaving.password || isSaving.image ? (
                  <span className="teacher-button-loader"></span>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} /> Save Changes
                  </>
                )}
              </button>
              <button
                type="button"
                className="teacher-cancel-button"
                onClick={handleCancel}
                disabled={isSaving.profile || isSaving.password || isSaving.image}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default Settings;